import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  OneLineList,
  SkeletonCard,
} from "../../../components/ui";
import type { Client } from "../../clients/types/clients.types";
import { CLIENT_STATUS_STYLES } from "../../clients/statusStyles";

const COLUMNS = ["Client name", "Goal", "Activity", "Joined on", "Status"];

/** The card's own footprint — its heading, the column headers, and the handful
 *  of rows the endpoint sends — so the block that stands in for it is the size
 *  of the card that lands in its place. */
const FOOTPRINT = "h-[370px] sm:h-[390px]";

/** The same footprint as a floor rather than a ceiling. The endpoint decides how
 *  many rows it sends, and pinned to the skeleton's exact height the card cut the
 *  last of them off below its own border. */
const MIN_FOOTPRINT = "min-h-[370px] sm:min-h-[390px]";

interface RecentClientsTableProps {
  /** The newest clients, in the order the dashboard endpoint returned them. */
  clients: Client[];
  /** Stands in for the rows on a failure and when the list is empty. */
  empty: EmptyStateProps;
  /**
   * Where the screen is. The card is not drawn until it is ready — a heading
   * over an empty table reads as a workspace with nobody in it — and a wait
   * long enough to notice shimmers the whole card in its place.
   *
   * The phase alone decides it, rows in hand or not: it stands for the whole
   * screen rather than for this card's own data, and a table that filled the
   * moment its own call answered would land ahead of every card beside it.
   */
  phase?: LoadPhase;
}

const RecentClientsTable: React.FC<RecentClientsTableProps> = ({
  clients,
  empty,
  phase = "ready",
}) =>
  phase !== "ready" ? (
    <SkeletonCard phase={phase} className={FOOTPRINT} />
  ) : (
  <section
    className={`rounded-md border border-line bg-white p-4 sm:p-6 3xl:p-8 ${MIN_FOOTPRINT}`}
  >
    <h2 className="text-[16px] 3xl:text-[19px] font-medium text-ink">
      Recent client
    </h2>

    {/* Wide table scrolls inside its own container so the page never does. */}
    <div className="mt-4 overflow-x-auto card-inner-scrollbar">
      <table
        className={`w-full table-fixed border-collapse text-left ${
          // No min-width with nothing to lay out: it would push the empty
          // state past a phone’s edge — see `ClientsTable`.
          clients.length === 0 ? "" : "min-w-[720px]"
        }`}
      >
        {/* Explicit widths — auto layout sized every column to its content and
            left the last two bunched up with dead space at the right edge. */}
        <colgroup>
          <col className="w-[32%]" />
          <col className="w-[24%]" />
          <col className="w-[14%]" />
          <col className="w-[15%]" />
          <col className="w-[15%]" />
        </colgroup>
        <thead>
          <tr className="border-b border-line">
            {COLUMNS.map((heading) => (
              <th
                key={heading}
                scope="col"
                className="pb-3 text-[13px] font-normal text-ink-muted"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        {clients.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={COLUMNS.length}>
                <EmptyState {...empty} />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {clients.map((client) => (
              <tr key={client.id} className="border-b border-line last:border-0">
                <td className="py-4">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-[#DDF3E4] flex items-center justify-center flex-shrink-0">
                      <span className="text-[13px] font-medium text-[#2F8E5B]">
                        {client.name.charAt(0)}
                      </span>
                    </span>
                    <span className="text-[14px] text-ink">{client.name}</span>
                  </div>
                </td>
                {/* Goals on one line, with a "+N" for the rest — the same
                    treatment the full roster's Goal column gives it, via
                    `OneLineList`. Two at most here: this column is narrower
                    than the roster's, and a client with several goals ran the
                    line up against the Activity column beside it. */}
                <td className="py-4 min-w-0 text-[14px] text-ink-muted">
                  <OneLineList items={client.goals} label="goals" max={2} />
                </td>
                <td className="py-4 text-[14px] text-ink-muted">
                  {client.activity}
                </td>
                <td className="py-4 text-[14px] text-ink-muted">
                  {client.joinedOn}
                </td>
                <td className="py-4">
                  <span
                    className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                      CLIENT_STATUS_STYLES[client.status]
                    }`}
                  >
                    {client.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  </section>
  );

export default RecentClientsTable;
